import { SagaIterator } from 'redux-saga';
import { call, put, takeEvery } from 'redux-saga/effects';

import { Set } from '~commonInfra/utils/Set';
import { AdjustAuthorConfiguredDCR } from '~src/bootstrap/redux/actions/AppActionTypes';
import { createMultipleItemResultNotificationValues, MultipleItemResultNotificationValues } from '~src/commonInfra/contentActions/ActionMessages';
import { ContentActionNotification } from '~src/commonInfra/contentActions/ContentActionNotification';
import { IErrorItem, MultipleItemResult } from '~src/commonInfra/contentActions/MultipleItemResult';
import { IError } from '~src/httpClient/serverApi/types/IError';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { ITagError } from '~src/httpClient/serverApi/types/ITagError';
import { IDCRAuthorConfigInfo } from '~src/spacesPlatform/models/IDCRAuthorConfigInfo';

import { Formatters, Messages } from '../../codegen/Localize';
import { authorConfiguredViewSucceeded } from '../actions/AuthorConfiguredViewsActions';
import { AuthorConfiguredDCRAction } from '../entityActions/AuthorConfiguredDCREntityActions';
import { IProcessedUpdateAuthorConfiguredViewResult, updateAuthorConfiguredViews } from '../serverApi/requests/UpdateAuthorConfiguredViewRequest';
import { DCRTelemetryEventSource, sendAuthorConfiguredDCRTelemetry } from '../telemetry/DCRTelemetry';
import { IProcessedViewMonitoringStatusInfo } from '../types/AuthorConfiguredDCRTypes';

export function* handleAdjustAuthorConfiguedDCRAction(action: AuthorConfiguredDCRAction): SagaIterator {
  if (!action.selectedContent[0]?.luid || !action.selectedContent[0]?.id) {
    return;
  }

  const selectedContent = action.selectedContent[0];

  try{
    const result: IProcessedUpdateAuthorConfiguredViewResult = yield call(updateAuthorConfiguredViews, { should_monitor: action.shouldMonitor, view_luid: selectedContent.luid });
    if (!(result.errors?.length > 0) && result.viewMonitoringStatusInfo) {
      yield put(authorConfiguredViewSucceeded(result.viewMonitoringStatusInfo));
    }

    const notificationResult = getNotificationResult(result, selectedContent.id);
    yield call(showNotification, notificationResult, action.selectedContent, action.shouldMonitor);
  } catch (e) {
    // This should not happen, but is here just in case. Mocking the result since there isn't one in this scenario
    const mockResult: IResult = {
      errors: [
        {
          id: selectedContent.id,
          code: 0 // Could be any
        },
      ],
    };
    yield call(showNotification, mockResult, action.selectedContent, action.shouldMonitor);
  } finally {
    yield call(
      sendAuthorConfiguredDCRTelemetry,
      action.shouldMonitor,
      selectedContent.luid,
      DCRTelemetryEventSource.ViewActionMenu
    );
  }
}

export function convertITagErrorsToIErrors(tagErrors: ITagError[], id: string): IError[] {
    return tagErrors.map((error) => {
      return {
        id,
        code: error.httpErrorCode,
        message: error.message,
      };
    });
}

export interface INotificationResult extends IResult {
  viewMonitoringStatusInfo: IProcessedViewMonitoringStatusInfo;
  errors?: IError[];
}

export function getNotificationResult(result: IProcessedUpdateAuthorConfiguredViewResult, viewId: string): INotificationResult {
  let notificationResult: INotificationResult = { viewMonitoringStatusInfo: result.viewMonitoringStatusInfo };
  if (result.errors?.length > 0) {
    notificationResult.errors = convertITagErrorsToIErrors(result.errors, viewId);
  }
  return notificationResult;
}

export function getSuccessSummary(successCount: number, shouldMonitor: boolean): string {
  return shouldMonitor
    ? Messages.turnOnAuthorConfiguredDCRForViewSuccess({ITEM_COUNT: successCount, ITEM_COUNT_LOC: Formatters.formatCount(successCount)})
    : Messages.turnOffAuthorConfiguredDCRForViewSuccess({ITEM_COUNT: successCount, ITEM_COUNT_LOC: Formatters.formatCount(successCount)});
}

export function getErrorSummary(successCount: number, shouldMonitor: boolean): string {
  return shouldMonitor
    ? Messages.turnOnAuthorConfiguredDCRForViewError({ERROR_COUNT: successCount, ERROR_COUNT_LOC: Formatters.formatCount(successCount)})
    : Messages.turnOffAuthorConfiguredDCRForViewError({ERROR_COUNT: successCount, ERROR_COUNT_LOC: Formatters.formatCount(successCount)});
}

export function showNotification(
  result: INotificationResult,
  contents: IDCRAuthorConfigInfo[],
  shouldMonitor: boolean
) {
  const notification: MultipleItemResultNotificationValues =
    createMultipleItemResultNotificationValues(
      new MultipleItemResult(Set.fromIdItemArray(contents), result),
      {
        describeSuccessSummary: (successCount: number) => {
          return getSuccessSummary(successCount, shouldMonitor);
        },
        describeErrorSummary: (successCount: number) => {
          return getErrorSummary(successCount, shouldMonitor);
        },
        // TODO: fill in error details once they are sent back from the API
        describeErrorDetail: (errorItem: IErrorItem<IDCRAuthorConfigInfo>) => {
          const description = {
            itemName: errorItem.item?.name,
            errorMessage: getErrorMessageFor(errorItem.error.code, shouldMonitor),
          };
          return description;
        },
      }
    );
  ContentActionNotification.displayItemizedToastsForResult(notification);
}

export function getErrorMessageFor(errorCode: number, shouldMonitor: boolean): string {
  switch (errorCode) {
    // TODO: Set up error code responses when they are ready
    default:
      return getErrorSummary(1, shouldMonitor);
  }
}

export function* rootAdjustAuthorConfiguredDCRSaga(): SagaIterator {
  yield takeEvery(AdjustAuthorConfiguredDCR, handleAdjustAuthorConfiguedDCRAction);
}